<script setup lang="ts">
import type { Tables } from '~~/types/database.types.ts';
import { useStorage, ref, type PropType } from '#imports';

const { formatedImageUrl } = useStorage();

type Profiles = Tables<'profiles'>[];
type Spots = Tables<'spots'>[];
type Maps = Tables<'maps'>[];

const props = defineProps({
  data: {
    type: Array as PropType<Profiles | Spots | Maps>,
    required: true,
  },
});

const carousel = ref(null);
const currentIndex = ref(4);

const changeSlide = (delta: number) => {
  let newIndex = currentIndex.value + delta;

  // Wrap around to the end if going below 0
  if (newIndex < 0) {
    newIndex = props.data.length - 1;
  }
  // Wrap around to the beginning if exceeding array length
  else if (newIndex >= props.data.length) {
    newIndex = 0;
  }

  currentIndex.value = newIndex;
};
</script>

<template>
<div class="carousel-container">
  <div class="carousel-wrapper">
    <div ref="carousel" class="carousel">
      <div
        v-for="(item, index) in props.data"
        :key="item.id"
        class="carousel-item"
        :class="{
          'center': index === currentIndex,
          'left-1': index === currentIndex - 1,
          'left-2': index === currentIndex - 2,
          'right-1': index === currentIndex + 1,
          'right-2': index === currentIndex + 2
        }"
      >
        <NuxtLinkLocale
          :key="item.id"
          :to="`/maps/${item.id}`"
          :style="`background-image: url('${formatedImageUrl(item.cover_image_url || item.profile_image_url, 350, 400, 80)}')`"
          class="map-carousel-item"
        >
          <span class="carousel-item-title">
          {{ item.title }}
          </span>
          <span class="carousel-item-spots">
            {{ item.spots_count }} Spots
          </span>
        </NuxtLinkLocale>
      </div>
    </div>
    <br>
  </div>
  <div class="carousel-buttons">
    <MPButton
      icon="ri-arrow-left-line"
      link
      label="Previous"
      hide-label
      rounded
      background-color="var(--color-gray-300)"
      @click="changeSlide(-1)"
    />
    <div v-if="data[0].review">
      <div class="carousel-review">
        <div class="carousel-review-profile-image">
          <MPImg
            :src="data[0].profile.profile_image_url"
          />
          <div class="carousel-review-profile-name">
            Map by @{{ data[0].profile.mappr_handle }}
          </div>
          <div class="carousel-review-review-text">
            {{ data[0].review }}
          </div>
      </div>
      </div>
    </div>
    <MPButton
      icon="ri-arrow-right-line"
      fill="black"
      label="Next"
      hide-label
      link
      rounded
      background-color="var(--color-gray-300)"
      @click="changeSlide(1)"
    />
  </div>
</div>
</template>

<style scoped lang="postcss">
@import "@/assets/css/breakpoints.pcss";
@import "@/assets/css/typography.pcss";
.carousel-wrapper {
  position: relative;
  width: 100%;
  height: 18rem;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 5000px;
  margin-block: 5rem;
}

.carousel {
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
}

.carousel-item {
  position: absolute;
  aspect-ratio: 3/4;
  height: 18rem;
  left: 50%;
  transition: all 0.5s ease-in-out;
  opacity: 0;
  transform: translateX(-50%) translateZ(0);
  @mixin screen sm {
    height: 20rem;
  }

  @mixin screen lg {
    height: 22rem;
  }
}

.carousel-item:before {
  position: absolute;
  content: "";
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: linear-gradient(180deg, rgba(0, 0, 0, 0.00) 10%, rgba(0, 0, 0, 0.80) 100%);
  border-radius: 1rem;
}

.carousel-item.center {
  opacity: 1;
  transform: translateX(-50%) translateZ(200px) scale(1.2);
  z-index: 5;
}

.carousel-item.left-1 {
  opacity: 0.8;
  transform: translateX(-130%) translateZ(100px) scale(0.9);
  z-index: 4;
}

.carousel-item.left-2 {
  opacity: 0.6;
  transform: translateX(-190%) translateZ(0) scale(0.8);
  z-index: 3;
}

.carousel-item.right-1 {
  opacity: 0.8;
  transform: translateX(30%) translateZ(100px) scale(0.9);
  z-index: 4;
}

.carousel-item.right-2 {
  opacity: 0.6;
  transform: translateX(90%) translateZ(0) scale(0.8);
  z-index: 3;
}

.map-carousel-item {
  display: flex;
  flex-direction: column;
  justify-content: end;
  text-align: left;
  width: 100%;
  height: 100%;
  border-radius: 0.5rem;
  background-size: cover;
  background-position: center;
  padding: 1rem 2rem 1rem 1rem;
  color: white;
  text-decoration: none;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.carousel-item-title {
  @mixin typeface-summary;
  font-weight: 400;
  z-index: 1;
}

.carousel-item-spots {
  @mixin typeface-small;
  margin-top: 1rem;
  z-index: 1;
  width: fit-content;
  border-radius: 2rem;
  border: 1px solid var(--color-slate-200);
  background-color: rgba(0,0,0,0.5);
  padding: 0.25rem 1rem;
}

.carousel-buttons {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 1rem;
  @mixin screen sm {
    margin-top: 8rem
  }
}
</style>
